import React, { useCallback } from "react";
import { Badge, Banner, Button, Card, SkeletonBodyText } from "@shopify/polaris";
import useSupportResource from "../../hooks/useSupportResource";
import { getSupportSyncHealth } from "../../services/supportApi";
import {
  formatCount,
  formatDateTime,
  formatRelativeTime,
  readableStatus,
} from "./supportFormatters";
import "./SyncEngineHealthPanel.css";

const POLL_INTERVAL_MS = 30_000;

const statusTone = (status) => {
  switch (String(status || "UNKNOWN").toUpperCase()) {
    case "HEALTHY":
      return "success";
    case "DELAYED":
    case "STALE":
      return "warning";
    case "NEEDS_ATTENTION":
    case "UNHEALTHY":
      return "critical";
    default:
      return "info";
  }
};

const laneValue = (value, telemetryAvailable) =>
  telemetryAvailable ? formatCount(value, "Not recorded") : "No telemetry";

const evidenceTime = (value, telemetryAvailable, emptyLabel = "Not recorded") =>
  telemetryAvailable ? formatDateTime(value, emptyLabel) : "No telemetry";

const isIdleWorkload = (workload) =>
  workload?.telemetryAvailable === true &&
  String(workload?.status || "").toUpperCase() === "HEALTHY" &&
  [
    workload?.queued,
    workload?.blocked,
    workload?.processing,
    workload?.retrying,
    workload?.needsAttention,
    workload?.succeededLast24Hours,
  ].every((value) => value !== null && value !== undefined && Number(value) === 0);

const WorkloadTable = ({ workloads, label, canonicalEvidence }) => (
  <div className="sync-health-panel__workload-table-wrap">
    <table aria-label={label}>
      <thead>
        <tr>
          <th scope="col">Workload</th>
          <th scope="col">Health</th>
          <th scope="col">Queued</th>
          <th scope="col">Blocked / audit</th>
          <th scope="col">Processing</th>
          <th scope="col">Retrying</th>
          <th scope="col">Merchant action needed</th>
          <th scope="col">Succeeded · 24h</th>
          <th scope="col">Oldest queued</th>
          <th scope="col">Active-job heartbeat</th>
        </tr>
      </thead>
      <tbody>
        {workloads.map((workload) => {
          const available = workload?.telemetryAvailable === true;
          return (
            <tr key={workload?.workloadType || "unknown"}>
              <th scope="row">{readableStatus(workload?.workloadType)}</th>
              <td>
                <Badge status={statusTone(workload?.status)}>
                  {isIdleWorkload(workload)
                    ? "Healthy · Idle"
                    : readableStatus(workload?.status)}
                </Badge>
              </td>
              <td>{laneValue(workload?.queued, available)}</td>
              <td>{laneValue(workload?.blocked, available)}</td>
              <td>{laneValue(workload?.processing, available)}</td>
              <td>{laneValue(workload?.retrying, available)}</td>
              <td>{laneValue(workload?.needsAttention, available)}</td>
              <td>{laneValue(workload?.succeededLast24Hours, available)}</td>
              <td>
                {available
                  ? formatRelativeTime(workload?.oldestQueuedAt, "No queued work")
                  : "No telemetry"}
              </td>
              <td>
                {available
                  ? canonicalEvidence
                    ? formatRelativeTime(workload?.activeJobHeartbeatAt, "No active job")
                    : "Not recorded by legacy ledger"
                  : "No telemetry"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const SyncEngineHealthPanel = ({ shopId, merchantAccountId }) => {
  const load = useCallback(
    ({ signal } = {}) =>
      getSupportSyncHealth({ shopId, merchantAccountId, signal }),
    [shopId, merchantAccountId]
  );
  const resourceKey = `support-sync-health:${shopId ?? "global"}:${merchantAccountId ?? "all"}`;
  const { data, loading, refreshing, error, lastSuccessfulAt, refresh } =
    useSupportResource({
      load,
      resourceKey,
      pollIntervalMs: POLL_INTERVAL_MS,
    });

  if (loading && !data) {
    return (
      <Card>
        <div className="sync-health-panel" aria-label="Loading sync engine health">
          <h2>Sync engine health</h2>
          <SkeletonBodyText lines={5} />
        </div>
      </Card>
    );
  }

  const status = data?.status || "UNKNOWN";
  const telemetryAvailable = data?.telemetryAvailable === true;
  const lanes = data?.lanes || {};
  const freshness = data?.freshness || {};
  const workloads = Array.isArray(data?.workloads) ? data.workloads : [];
  const workerTelemetryAvailable = Boolean(freshness.workerHeartbeatAt);
  const workerStatus = freshness.workerStatus || "UNKNOWN";
  const canonicalTelemetryAvailable = String(data?.telemetrySource || "").includes("CANONICAL");
  const legacyPayloadRecovery = data?.legacyPayPalPayloadRecovery;
  const oldestQueuedLabel = telemetryAvailable
    ? Number(lanes.queued) === 0
      ? "No queued work"
      : data?.oldestQueuedAt
      ? `${formatRelativeTime(data.oldestQueuedAt)} · ${formatDateTime(data.oldestQueuedAt)}`
      : "Timestamp not recorded"
    : "No telemetry";

  return (
    <Card>
      <section className="sync-health-panel" aria-labelledby="sync-engine-health-heading">
        <div className="sync-health-panel__heading">
          <div>
            <div className="sync-health-panel__title-row">
              <h2 id="sync-engine-health-heading">Sync engine health</h2>
              <Badge status={statusTone(status)}>{readableStatus(status)}</Badge>
            </div>
            <p>{data?.summary || "Sync health cannot be confirmed."}</p>
            <span>
              Snapshot {formatDateTime(data?.observedAt, "not available")}
              {lastSuccessfulAt
                ? ` · refreshed ${formatRelativeTime(lastSuccessfulAt)}`
                : ""}
              {" · auto-refreshes every 30 seconds"}
            </span>
            <span className="sync-health-panel__source">
              Evidence source: {readableStatus(data?.telemetrySource, "No telemetry")}
            </span>
          </div>
          <Button onClick={() => refresh()} loading={refreshing}>
            Refresh sync health
          </Button>
        </div>

        {error ? (
          <Banner title="The latest sync-health refresh failed" status="warning">
            <p>
              {error}
              {data ? ". Showing the last successful snapshot." : ""}
            </p>
          </Banner>
        ) : null}

        {!telemetryAvailable ? (
          <div className="sync-health-panel__unknown" role="status">
            <strong>No telemetry</strong>
            <span>
              Persisted fulfillment ledger data is not available for this scope. No zero values
              are assumed.
            </span>
          </div>
        ) : null}

        <ol className="sync-health-panel__lanes" aria-label="Persisted sync work lanes">
          <li><span>Queued</span><strong>{laneValue(lanes.queued, telemetryAvailable)}</strong></li>
          <li><span>Blocked / audit</span><strong>{laneValue(lanes.blocked, telemetryAvailable)}</strong></li>
          <li><span>Processing</span><strong>{laneValue(lanes.processing, telemetryAvailable)}</strong></li>
          <li><span>Retrying</span><strong>{laneValue(lanes.retrying, telemetryAvailable)}</strong></li>
          <li><span>Merchant action needed</span><strong>{laneValue(lanes.needsAttention, telemetryAvailable)}</strong></li>
          <li><span>Succeeded · 24h</span><strong>{laneValue(lanes.succeededLast24Hours, telemetryAvailable)}</strong></li>
        </ol>

        {legacyPayloadRecovery?.dataAvailable === true ? (
          <section
            className="sync-health-panel__legacy-recovery"
            aria-labelledby="legacy-payload-recovery-heading"
          >
            <div>
              <h3 id="legacy-payload-recovery-heading">Retired PayPal payload recovery</h3>
              <p>
                One-time, processor-owned recovery for failures created by the retired
                tracking payload. These counts are not added to the live lanes above.
              </p>
            </div>
            <dl>
              <div><dt>Waiting for processor</dt><dd>{formatCount(legacyPayloadRecovery.queued)}</dd></div>
              <div><dt>Recovered</dt><dd>{formatCount(legacyPayloadRecovery.succeeded)}</dd></div>
              <div><dt>Needs separate diagnosis</dt><dd>{formatCount(legacyPayloadRecovery.failed)}</dd></div>
              <div>
                <dt>Last admitted</dt>
                <dd>{formatDateTime(legacyPayloadRecovery.lastAdmittedAt, "Not recorded")}</dd>
              </div>
              <div>
                <dt>Last completed</dt>
                <dd>{formatDateTime(legacyPayloadRecovery.lastCompletedAt, "Not recorded")}</dd>
              </div>
            </dl>
          </section>
        ) : null}

        <div className="sync-health-panel__workloads">
          <div className="sync-health-panel__subheading">
            <h3>Workload health</h3>
            <p>
              {canonicalTelemetryAvailable
                ? "Canonical onboarding, merchant history, and realtime delivery lanes."
                : workloads.some((workload) => workload?.telemetryAvailable === true)
                ? "Legacy workload evidence is derived only from explicit webhook sources and back-in-time request identifiers. Unclassified rows remain unknown."
                : "Per-workload evidence is not provable from the available legacy rows; the aggregate legacy ledger above remains authoritative."}
            </p>
          </div>
          {workloads.length ? (
            <WorkloadTable
              workloads={workloads}
              label="Workload health"
              canonicalEvidence={canonicalTelemetryAvailable}
            />
          ) : (
            <p className="sync-health-panel__workload-empty" role="status">
              No workload telemetry is available.
            </p>
          )}
        </div>

        <dl className="sync-health-panel__evidence">
          <div><dt>Oldest queued</dt><dd>{oldestQueuedLabel}</dd></div>
          <div><dt>Last accepted</dt><dd>{evidenceTime(data?.lastAcceptedAt, telemetryAvailable)}</dd></div>
          <div><dt>Last attempt</dt><dd>{evidenceTime(data?.lastAttemptAt, telemetryAvailable)}</dd></div>
          <div><dt>Last success</dt><dd>{evidenceTime(data?.lastSucceededAt, telemetryAvailable)}</dd></div>
          <div>
            <dt>Processor heartbeat</dt>
            <dd>
              <Badge status={statusTone(workerStatus)}>{readableStatus(workerStatus)}</Badge>
              <span>
                {workerTelemetryAvailable
                  ? ` ${formatRelativeTime(freshness.workerHeartbeatAt)} · ${formatDateTime(
                      freshness.workerHeartbeatAt
                    )}`
                  : " No telemetry"}
              </span>
            </dd>
          </div>
          <div>
            <dt>Processor release</dt>
            <dd>{freshness.workerReleaseVersion || "No telemetry"}</dd>
          </div>
          <div>
            <dt>Required listeners</dt>
            <dd>
              <Badge status={statusTone(freshness.listenerStatus)}>
                {readableStatus(freshness.listenerStatus)}
              </Badge>
              {freshness.listenerIssue ? ` ${freshness.listenerIssue}` : ""}
            </dd>
          </div>
          <div>
            <dt>Latest listener failure</dt>
            <dd>{formatDateTime(freshness.listenerLastFailureAt, "Not recorded")}</dd>
          </div>
        </dl>
      </section>
    </Card>
  );
};

export default SyncEngineHealthPanel;
